import React from "react";
import { graphql } from "gatsby";

import Layout from "../components/Layout";
import Seo from "../components/Seo";
import Header from "../components/Header";
import DynamicChildren from "../components/DynamicChildren";

import { hydrateBlogPosts } from "../utils/utils";

import "../styles/pages/blog.scss";

const BlogLanding = ({ pageContext, location, data }) => {
    const { page } = data;
    const { header, dynamicComponents } = page;
    const recentBlogPosts = hydrateBlogPosts(data.recentBlogPosts);

    return (
        <Layout className="blog" location={location}>
            <Header banner={page?.banner} header={header} location={location} />

            <DynamicChildren
                contentfulComponents={dynamicComponents}
                blogPost={data.blogPost}
                pageContext={pageContext}
                recentBlogPosts={recentBlogPosts}
            />
        </Layout>
    );
};

export const Head = ({ data }) => {
    const blogPost = data.blogPost;
    const metadata = [];

    if (blogPost.image?.url) {
        metadata.push({
            key: "og:image",
            property: "og:image",
            content: blogPost.image.url,
        });
    }
    return (
        <Seo
            defaultTitle={`${blogPost.title} | Shiftmed Blog`}
            page={data.page}
            seoRobots={blogPost.seoRobots}
            seoDescription={blogPost.seoDescription}
            meta={metadata}
        />
    );
};

export default BlogLanding;

export const PageQuery = graphql`
    query BlogPostPageQuery($slug: String) {
        page: contentfulPage(path: { eq: "/blog/post" }) {
            header {
                ...HeaderData
            }
            banner {
                ...Banner
            }
            ...DynamicComponents
            seoTitle
            seoDescription {
                seoDescription
            }
            seoRobots
        }
        blogPost: contentfulArticle(slug: { eq: $slug }) {
            slug
            seoDescription
            publishDate
            hideImageOnFullPage
            image {
                gatsbyImageData(layout: CONSTRAINED)
                title
                description
                file {
                    contentType
                    url
                }
                url
            }
            title
            author {
                name
            }
            topics {
                slug
                name
                article {
                    slug
                }
            }
            body {
                raw
                references {
                    ... on ContentfulAsset {
                        __typename
                        contentful_id
                        file {
                            contentType
                            url
                        }
                        title
                        description
                    }
                    ... on ContentfulFrame {
                        __typename
                        contentful_id
                        title
                        url
                        hide
                        mobileImage {
                            gatsbyImageData(layout: CONSTRAINED)
                            title
                            description
                            file {
                                contentType
                                url
                            }
                        }
                    }
                    ... on ContentfulJumperLink {
                        __typename
                        contentful_id
                        anchorLink
                        title
                    }
                    ... on ContentfulSingleVideo {
                        __typename
                        contentful_id
                        url
                        title
                    }
                    ... on ContentfulForm {
                        __typename
                        contentful_id
                        formType
                        lead {
                            raw
                        }
                        successMessage {
                            raw
                        }
                        submittedFormLink {
                            label
                            color
                            url
                            icon {
                                gatsbyImageData(layout: CONSTRAINED)
                                title
                                description
                                file {
                                    contentType
                                    url
                                }
                            }
                            downloadAsset {
                                filename
                                url
                            }
                        }
                    }
                }
            }
            fadeBodyText
            seoRobots
        }
        recentBlogPosts: allContentfulArticle(
            filter: {
                dummy: { ne: true }
                slug: { ne: $slug }
                type: { eq: "Blog Post" }
            }
            sort: { publishDate: DESC }
            limit: 3
        ) {
            edges {
                node {
                    slug
                    publishDate
                    image {
                        gatsbyImageData(layout: CONSTRAINED)
                        title
                        description
                        file {
                            contentType
                            url
                        }
                    }
                    title
                }
            }
        }
    }
`;
